import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../api";
import { symbolFor } from "../utils/currency";

const FxContext = createContext();

export const FxProvider = ({ children }) => {
  const [rates, setRates] = useState(null); // USD-base map
  const [displayCurrency, setDisplayCurrencyState] = useState(() => localStorage.getItem("lp-display-currency") || "");

  useEffect(() => {
    api.get("/fx/rates").then((r) => setRates(r.data.rates)).catch(() => setRates(null));
  }, []);

  const setDisplayCurrency = (code) => {
    setDisplayCurrencyState(code || "");
    if (code) localStorage.setItem("lp-display-currency", code);
    else localStorage.removeItem("lp-display-currency");
  };

  // Returns an "≈ SYM 1,234.56" string, or null when conversion is off / not possible
  const approx = (amount, fromCode) => {
    if (amount == null || !displayCurrency || !rates) return null;
    const from = fromCode || "USD";
    if (from === displayCurrency) return null;
    const rFrom = rates[from];
    const rTo = rates[displayCurrency];
    if (!rFrom || !rTo) return null;
    const converted = (Number(amount) * rTo) / rFrom;
    const formatted = converted.toLocaleString(undefined, {
      minimumFractionDigits: converted >= 1000 ? 0 : 2,
      maximumFractionDigits: converted >= 1000 ? 0 : 2,
    });
    return `≈ ${symbolFor(displayCurrency)}${formatted}`;
  };

  // Format an amount converted from fromCode into an explicit target currency, e.g. "TT$1,234".
  const convert = (amount, fromCode, toCode) => {
    if (amount == null || !toCode || !rates) return null;
    const from = fromCode || "USD";
    const rFrom = rates[from];
    const rTo = rates[toCode];
    if (!rFrom || !rTo) return null;
    const converted = (Number(amount) * rTo) / rFrom;
    const formatted = converted.toLocaleString(undefined, {
      minimumFractionDigits: converted >= 1000 ? 0 : 2,
      maximumFractionDigits: converted >= 1000 ? 0 : 2,
    });
    return `${symbolFor(toCode)}${formatted}`;
  };

  return (
    <FxContext.Provider value={{ rates, displayCurrency, setDisplayCurrency, approx, convert }}>
      {children}
    </FxContext.Provider>
  );
};

export const useFx = () => useContext(FxContext);
